import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import storageSession from "redux-persist/es/storage/session";
import { persistReducer } from "redux-persist";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import { CreditCounts, QcCreditMultipliers } from "../services/HigherAwardTracking/HigherAwardTrackingAPI.types";
interface UplineDiamondData {
  [tabId: string]: {
    uplineMap: Record<string, string>;
    loadedPages: number[];
  };
}
interface AboTrackingInfo {
  aboName: string;
  aboNum: string;
  affNum: string;
  country: string;
  qcCreditCounts: CreditCounts;
  qcCreditMultipliers: QcCreditMultipliers[];
  isPlatinumLeg: boolean;
}

interface GlobalAwardQCDetails {
  periodList: number[];
  viewType: "desktop" | "mobile";
}

interface HigherAwardTrackingReducer {
  aboTrackingInfo: AboTrackingInfo;
  qcDetails: GlobalAwardQCDetails;
  uplineDiamondData: UplineDiamondData;
  hasAgreedToHatTerms: boolean;
  hatOverview: {
    period: string;
  };
}

const initialHigherAwardTracking: HigherAwardTrackingReducer = {
  aboTrackingInfo: {
    isPlatinumLeg: false,
    aboNum: "",
    affNum: "",
    aboName: "",
    country: "",
    qcCreditCounts: {
      NotCounting: 0,
      Counting: 0,
      Total: "",
    },
    qcCreditMultipliers: [],
  },
  uplineDiamondData: {},
  qcDetails: {
    periodList: [],
    viewType: "desktop",
  },
  hasAgreedToHatTerms: false,
  hatOverview: {
    period: "",
  },
};

const { actions, reducer } = createSlice({
  name: "aboTrackingInfo",
  initialState: initialHigherAwardTracking,
  reducers: {
    setAboTrackingInfo: (state, action: PayloadAction<AboTrackingInfo>) => {
      const { payload } = action;
      return {
        ...state,
        aboTrackingInfo: payload,
      };
    },
    setGlobalAwardPeriods: (state, action: PayloadAction<{ periodList: number[] }>) => {
      return {
        ...state,
        qcDetails: {
          ...state.qcDetails,
          periodList: action.payload.periodList,
        },
      };
    },
    setGlobalAwardViewType: (state, action: PayloadAction<{ viewType: "mobile" | "desktop" }>) => {
      return {
        ...state,
        qcDetails: {
          ...state.qcDetails,
          viewType: action.payload.viewType,
        },
      };
    },
    setUplineMapForTab: (state, action: PayloadAction<{ tabId: string; uplineMap: Record<string, string> }>) => {
      const { tabId, uplineMap } = action.payload;
      state.uplineDiamondData[tabId] ??= { uplineMap: {}, loadedPages: [] };
      state.uplineDiamondData[tabId].uplineMap = uplineMap;
    },
    setLoadedPagesForTab: (state, action: PayloadAction<{ tabId: string; loadedPages: number[] }>) => {
      const { tabId, loadedPages } = action.payload;
      state.uplineDiamondData[tabId] ??= { uplineMap: {}, loadedPages: [] };
      state.uplineDiamondData[tabId].loadedPages = loadedPages;
    },
    setAgreedToHatTerms: (state, action: PayloadAction<{ agreed: boolean }>) => {
      return {
        ...state,
        hasAgreedToHatTerms: action.payload.agreed,
      };
    },
    setHatOverviewPeriod: (state, action: PayloadAction<{ period: string }>) => {
      return {
        ...state,
        hatOverview: {
          period: action.payload.period,
        },
      };
    },
  },
});

export const {
  setAboTrackingInfo,
  setUplineMapForTab,
  setLoadedPagesForTab,
  setGlobalAwardPeriods,
  setGlobalAwardViewType,
  setAgreedToHatTerms,
  setHatOverviewPeriod,
} = actions;

const persistConfig = {
  key: "aboTrackingInfo",
  storage: storageSession,
  stateReconciler: autoMergeLevel2,
};

export default persistReducer<HigherAwardTrackingReducer>(persistConfig, reducer);
